import {
  FILE_TABLE_ACTION_SYNC_VERSION,
  FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
} from "core/consts";
import { FileForm } from "core/types";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import {
  FileUploadStates,
  useUploadFile,
} from "dsl/ecosystems/PatientFiles/useUploadFile";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslations } from "translations";
import { useDeleteFile } from "./useDeleteFile";
import { useFetchFormDataAndGeneratePdf } from "./useFetchFormDataAndGeneratePdf";

export function useUploadAndShareForm({
  action,
  externalId,
  handleDeleteFile,
  handleUploadFile,
  setAction,
  setUploadStates,
  skip,
}: {
  action: GeneralListAction<FileFeatureData> | undefined;
  externalId: string;
  handleDeleteFile: ReturnType<typeof useDeleteFile>["handleDeleteFile"];
  handleUploadFile: ReturnType<typeof useUploadFile>["handleUploadFile"];
  setAction: SetAction<FileFeatureData>;
  setUploadStates: Dispatch<SetStateAction<FileUploadStates>>;
  skip: boolean;
}) {
  const toast = useToast();
  const translations = useTranslations();
  const fetchFormDataAndGeneratePdf = useFetchFormDataAndGeneratePdf({
    externalId,
  });

  useEffect(() => {
    if (
      (action?.type === FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM ||
        action?.type === FILE_TABLE_ACTION_SYNC_VERSION) &&
      !skip &&
      action?.data
    ) {
      const files = Array.isArray(action.data)
        ? (action.data as FileForm[])
        : [action.data as FileForm];
      const actionType = action.type;

      const uploadFilesInParallel = async () => {
        await Promise.all(
          files.map(async (file) => {
            const id = `form_${file.id}_${file.recare_file_type}_${Date.now()}`;

            const fileState = {
              cancel: async () => {},
              data: {
                category: file.category,
                category_other: file.category_other,
                content: "",
                file: new File([], ""),
                fileName: file.file_name?.decrypted ?? "",
                kis_document_id_string: file.kis_document_id_string,
                recare_file_type: file.recare_file_type,
                share_mode: file.share_mode,
              },
              error: null,
              fileId: -1,
              isCancelled: false,
              loading: true,
              timeout: undefined,
            };

            setUploadStates((prev) => ({
              ...prev,
              [id]: fileState,
            }));

            try {
              const { fileName, pdfBytes } = await fetchFormDataAndGeneratePdf({
                file,
              });

              const pdfArray = new Uint8Array(pdfBytes);
              const pdfFile = new File([pdfArray], fileName, {
                type: "application/pdf",
              });

              const base64Content = btoa(
                Array.from(pdfBytes, (byte) =>
                  String.fromCharCode(byte as number),
                ).join(""),
              );

              setUploadStates((prev) => {
                const { [id]: _removed, ...rest } = prev;
                return rest;
              });

              await handleUploadFile({
                category: file.category,
                category_other: file.category_other,
                content: base64Content,
                file: pdfFile,
                fileName,
                kis_document_id_string: file.kis_document_id_string,
                recare_file_type: file.recare_file_type,
                share_mode: file.share_mode,
              });
              if (actionType === FILE_TABLE_ACTION_SYNC_VERSION && file.id) {
                await handleDeleteFile(file.id);
              }
            } catch (err) {
              console.error(
                "Unexpected form upload error:",
                (err as Error).message,
              );

              setUploadStates((prev) => {
                const { [id]: _removed, ...rest } = prev;
                return rest;
              });

              toast({
                color: "danger",
                message: translations.fileUploader.fileCouldNotFetch,
              });
            }
          }),
        );

        setAction(undefined);
      };

      uploadFilesInParallel();
    }
  }, [action?.type, skip]);
}
